/// <amd-module name="TFS/TenantPicker/Components/TenantPanel/TenantPickerPanel" />
import * as React from "react";

import { VssComponent } from "VSS/Platform/Layout";

import { ITenantData, ITenantPickerData } from "TFS/TenantPicker/Contracts/TenantPickerDataModel";
import { format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IListItemDetails, IListRow, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import * as TenantPickerResources from "TFS/TenantPicker/Resources";

const tenantPickerDataProvider = "ms.vss-tfs-web.tenant-picker-data-provider";

export interface ITenantPickerPanelComponentState {
    data?: ITenantPickerData;
    showDirectoryList: boolean;
    showDescription: boolean;
    descriptionToShow: string;
}

export interface ITenantPickerPanelComponentProps {
    onDismiss: () => void;
}

export class TenantPickerPanelComponent extends VssComponent<ITenantPickerPanelComponentProps, ITenantPickerPanelComponentState> {
    private selection = new ListSelection(false);
    private tenants: ArrayItemProvider<ITenantData>;
    private selectedTenant?: ITenantData;
    private telemetryService: IVssTelemetryService;

    constructor(props: ITenantPickerPanelComponentProps) {
        super(props);
        this.state = {
            showDirectoryList: false,
            showDescription: false,
            descriptionToShow: TenantPickerResources.TenantPanelAADDescription
        };
    }

    public render(): JSX.Element {
        this.tenants = new ArrayItemProvider(this.state.data ? this.state.data.userTenantData.userTenants : []);

        return (
            <Panel
                lightDismiss={true}
                className="tenant-picker-panel"
                titleProps={{ text: TenantPickerResources.TenantPickerTitle }}
                onDismiss={this.props.onDismiss}
                footerButtonProps={[
                    {
                        text: TenantPickerResources.Cancel,
                        onClick: () => {
                            this.props.onDismiss();
                        }
                    },
                    {
                        text: TenantPickerResources.ChangeTenant,
                        primary: true,
                        onClick: this._onSwitchTenant,
                        disabled: !this.state.showDirectoryList || this.selectedTenant === undefined
                    }
                ]}
            >
                <div className="flex-column flex-grow">
                    {this.state.showDescription && this.state.data ? (
                        <>
                            <div>
                                <span>{format(this.state.descriptionToShow, this.state.data.user.tenant.displayName)}</span>
                                <span>{TenantPickerResources.SelectDirectoryDescription}</span>
                            </div>
                            {!this.state.data.user.isMsa ? (
                                <div>
                                    <div className="font-weight-semibold margin-top-16 margin-bottom-8">{TenantPickerResources.CurrentDirectory}</div>
                                    <div>{this.state.data.user.tenant.displayName}</div>
                                    <div className="secondary-text">{this.state.data.user.tenant.domain}</div>
                                    <div className="secondary-text">{this.state.data.user.tenant.id}</div>
                                </div>
                            ) : null}
                            <div className="flex-column tenant-picker-list">
                                <div className="font-weight-semibold margin-top-16">{TenantPickerResources.OtherDirectories}</div>
                                {this.state.showDirectoryList ? (
                                    <ScrollableList
                                        className="h-scroll-hidden"
                                        itemProvider={this.tenants}
                                        renderRow={this.renderRow}
                                        selection={this.selection}
                                        width="100%"
                                        selectableText={true}
                                        singleClickActivation={true}
                                        onSelect={this._onRowSelected}
                                    />
                                ) : (
                                    <MessageBar severity={MessageBarSeverity.Info}>{TenantPickerResources.SingleAccountDescription}</MessageBar>
                                )}
                            </div>
                        </>
                    ) : (
                        <Spinner label={TenantPickerResources.FetchingTenants} className="flex-grow justify-center">
                            {TenantPickerResources.FetchingTenants}
                        </Spinner>
                    )}
                </div>
            </Panel>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._fetchTenantData();
    }

    private _onSwitchTenant = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        if (this.selectedTenant) {
            this.telemetryService.publishEvent("MeControl", "TenantPicker", { Action: "Switch Tenants" });
            window.location.href = this.selectedTenant.authUrl;
        }
    };

    private renderRow = (index: number, item: ITenantData, details: IListItemDetails<ITenantData>, key?: string): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="flex-row h-scroll-hidden">
                    <div className="flex-column h-scroll-hidden tenant-picker-row">
                        <span className="text-ellipsis">{item.displayName}</span>
                        <span className="fontSizeMS font-size-ms text-ellipsis secondary-text">{item.id}</span>
                        <span className="fontSizeMS font-size-ms text-ellipsis secondary-text">{item.domain}</span>
                    </div>
                </div>
            </ListItem>
        );
    };

    private _onRowSelected = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<ITenantData>) => {
        if (this.selectedTenant === undefined) {
            this.forceUpdate();
        }
        this.selectedTenant = listRow.data;
    };

    private async _fetchTenantData() {
        if (!this.telemetryService) {
            this.telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        }

        this.telemetryService.publishEvent("MeControl", "TenantPicker", { Action: "Fetch Tenant Data" });
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<ITenantPickerData>(tenantPickerDataProvider);

        if (data) {
            this.telemetryService.publishEvent("MeControl", "TenantPicker", {
                Action: "Tenant Data Fetched",
                Tenant: data.user.tenant.id,
                IsMsa: data.user.isMsa,
                IsGuest: data.user.isGuestUserInAad,
                NumberOfTenants: data.userTenantData.userTenants.length
            });
            this.setState({
                data: data,
                showDescription: true,
                descriptionToShow: data.user.isMsa
                    ? TenantPickerResources.TenantPanelMSADescription
                    : TenantPickerResources.TenantPanelAADDescription,
                showDirectoryList: data.userTenantData.userTenants.length > 0
            });
        }
    }
}
